import { Component, Input, OnInit } from '@angular/core';

@Component({
  selector: 'app-mystery-box-content-list',
  templateUrl: './mystery-box-content-list.component.html',
  styleUrls: ['./mystery-box-content-list.component.sass']
})
export class MysteryBoxContentListComponent implements OnInit {

  @Input() record: any;
  @Input() field: any;

  constructor() { }

  ngOnInit(): void {
  }

  /**
   * Get display name for a content item
   * Priority: For Collections (entryType 1): itemCollectionRef > MysteryBoxContent.name
   *          For Items (entryType 2): lineItem > MysteryBoxContent.name
   *          Fallback: ID
   */
  getItemDisplayName(contentItem: any): string {
    if (!contentItem) return 'Unknown';

    // Priority 1: If entryType is Collection (1), use itemCollectionRef name first
    if (contentItem.entryType === 1 && contentItem.itemCollectionRef) {
      if (typeof contentItem.itemCollectionRef === 'object') {
        if (contentItem.itemCollectionRef.name) {
          return contentItem.itemCollectionRef.name;
        }
        if (contentItem.itemCollectionRef.displayName) {
          return contentItem.itemCollectionRef.displayName;
        }
      }
    }

    // Priority 2: If entryType is Item (2), try to get name from lineItem
    if (contentItem.entryType === 2 && contentItem.lineItem) {
      const lineItem = Array.isArray(contentItem.lineItem) 
        ? contentItem.lineItem[0] 
        : contentItem.lineItem;
      
      if (lineItem && lineItem.id) {
        // Try to get name from populated id field (most common case)
        if (typeof lineItem.id === 'object') {
          // Prefer displayName, then name
          if (lineItem.id.displayName) {
            return lineItem.id.displayName;
          }
          if (lineItem.id.name) {
            return lineItem.id.name;
          }
        }
      }
    }

    // Priority 3: Fallback to MysteryBoxContent's own name field
    if (contentItem.name && contentItem.name.trim() !== '') {
      return contentItem.name;
    }

    // Priority 4: Fallback to ID if available
    if (contentItem.id) {
      return `Content #${contentItem.id}`;
    }

    // Final fallback
    return 'Unknown';
  }

  /**
   * Get the link URL for a content item
   * Returns null if no link can be created
   */
  getItemLink(contentItem: any): string | null {
    if (!contentItem) return null;

    // Link to mystery box content page using the content item's ID
    if (contentItem.id) {
      return `/mystery-box-content/${contentItem.id}`;
    }

    // Fallback: try _id if id is not available
    if (contentItem._id) {
      return `/mystery-box-content/${contentItem._id}`;
    }

    return null;
  }

  /**
   * Check if content item has a link
   */
  hasLink(contentItem: any): boolean {
    return this.getItemLink(contentItem) !== null;
  }

  /**
   * Get weight for a content item
   */
  getItemWeight(contentItem: any): number {
    return contentItem.weight || 0;
  }

  /**
   * Sum of all weights across the mystery box's content array
   */
  getTotalWeight(): number {
    return this.getContentItems().reduce((sum, item) => sum + (item.weight || 0), 0);
  }

  /**
   * Normalized probability for a content item as a percentage (0–100)
   */
  getNormalizedOdds(contentItem: any): string {
    const total = this.getTotalWeight();
    if (!total) return '—';
    return ((contentItem.weight || 0) / total * 100).toFixed(2) + '%';
  }

  /**
   * Get coin weight name for a content item (only for collections)
   */
  getCoinWeightName(contentItem: any): string | null {
    if (!contentItem || contentItem.entryType !== 1) return null;
    
    if (contentItem.coinWeight) {
      if (typeof contentItem.coinWeight === 'object') {
        return contentItem.coinWeight.name || contentItem.coinWeight.displayName || null;
      }
    }
    return null;
  }

  /**
   * Get coin weight link for a content item (only for collections)
   */
  getCoinWeightLink(contentItem: any): string | null {
    if (!contentItem || contentItem.entryType !== 1) return null;
    
    if (contentItem.coinWeight) {
      if (typeof contentItem.coinWeight === 'object') {
        if (contentItem.coinWeight.id) {
          return `/nurture-rarity/${contentItem.coinWeight.id}`;
        }
      }
    }
    return null;
  }

  /**
   * Get diamond weight name for a content item (only for collections)
   */
  getDiamondWeightName(contentItem: any): string | null {
    if (!contentItem || contentItem.entryType !== 1) return null;
    
    if (contentItem.diamondWeight) {
      if (typeof contentItem.diamondWeight === 'object') {
        return contentItem.diamondWeight.name || contentItem.diamondWeight.displayName || null;
      }
    }
    return null;
  }

  /**
   * Get diamond weight link for a content item (only for collections)
   */
  getDiamondWeightLink(contentItem: any): string | null {
    if (!contentItem || contentItem.entryType !== 1) return null;
    
    if (contentItem.diamondWeight) {
      if (typeof contentItem.diamondWeight === 'object') {
        if (contentItem.diamondWeight.id) {
          return `/nurture-rarity/${contentItem.diamondWeight.id}`;
        }
      }
    }
    return null;
  }

  /**
   * Check if content item has sub-weights (only collections have sub-weights)
   */
  hasSubWeights(contentItem: any): boolean {
    return contentItem && contentItem.entryType === 1 && 
           (this.getCoinWeightName(contentItem) || this.getDiamondWeightName(contentItem));
  }

  /**
   * Check if content array exists and has items
   */
  hasContent(): boolean {
    return this.record && 
           this.record[this.field.key] && 
           Array.isArray(this.record[this.field.key]) && 
           this.record[this.field.key].length > 0;
  }

  /**
   * Get content items
   */
  getContentItems(): any[] {
    if (!this.hasContent()) {
      return [];
    }
    return this.record[this.field.key];
  }
}
